import React from "react";
import { Dialog, Menu, Transition, Disclosure } from "@headlessui/react";
import {
  Bars3BottomLeftIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
} from "@heroicons/react/20/solid";
import {
  AdjustmentsVerticalIcon,
  ArrowPathIcon,
  BarsArrowDownIcon,
  CalendarDaysIcon,
  CurrencyDollarIcon,
  HomeIcon,
} from "@heroicons/react/24/outline";
import { Fragment, useEffect, useState } from "react";
import { withTranslation } from "react-i18next";
import { AiOutlineDeploymentUnit } from "react-icons/ai";
import { BiCategory, BiUser } from "react-icons/bi";
import {
  MdOutlinePriceCheck,
  MdFeedback,
  MdOutlineTexture,
  MdProductionQuantityLimits,
  MdFormatShapes,
  MdVerifiedUser,
  MdSystemUpdate,
  MdTranslate,
  MdAttachMoney,
} from "react-icons/md";
import { useDispatch, useSelector } from "react-redux/es/exports";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { logOut } from "../features/user/userSlice";
import MainRoutes from "../routes/MainRoutes";
import DropdownLanguage from "./dropdownLanguage";
import { AiFillEdit } from "react-icons/ai";
import { BsCurrencyExchange } from "react-icons/bs";
import { FaFontAwesomeFlag } from "react-icons/fa";
import { getAkfaSummary } from "../services/akfa/akfaService";

const AKFA_BADGE_POLL_MS = 43200000;

const navigation = [
  { name: "sidebar_menu_dashboard", to: "/", icon: HomeIcon },
  {
    name: "sidebar_menu_currency",
    to: "/currency",
    icon: CurrencyDollarIcon,
  },
  {
    name: "sidebar_menu_exchange",
    to: "/exchange",
    icon: BsCurrencyExchange,
  },
  { name: "country", to: "/country", icon: FaFontAwesomeFlag },
  { name: "sidebar_menu_series", to: "/series", icon: BiUser },
  { name: "sidebar_material_type", to: "/material-type", icon: BiUser },
  { name: "sidebar_templates", to: "/templates", icon: BiUser },
  {
    name: "sidebar_menu_suppliers",
    to: "/suppliers",
    icon: CalendarDaysIcon,
  },
  {
    name: "sidebar_menu_textures",
    to: "/textures",
    icon: MdOutlineTexture,
  },
  {
    name: "sidebar_menu_templetes",
    to: "/templetes",
    icon: MdFormatShapes,
  },
  {
    name: "sidebar_menu_units",
    to: "/units",
    icon: AiOutlineDeploymentUnit,
  },
  {
    name: "sidebar_menu_products",
    icon: MdProductionQuantityLimits,
    to: "/products",
    children: [
      { name: "sidebar_menu_profiles", to: "/product/profile" },
      {
        name: "sidebar_menu_accessories",
        to: "/product/accessories",
      },
      { name: "sidebar_menu_seal", to: "/product/seal" },
      { name: "sidebar_menu_filling", to: "/product/filling" },
    ],
  },
  {
    name: "sidebar_menu_connection",
    to: "/connection",
    icon: MdOutlinePriceCheck,
  },
  {
    name: "sidebar_menu_insert",
    to: "/inserts",
    icon: BarsArrowDownIcon,
  },
  {
    name: "sidebar_menu_filling",
    to: "/filling",
    icon: AiFillEdit,
  },
  {
    name: "sidebar_menu_accessories",
    to: "/furniture",
    icon: AdjustmentsVerticalIcon,
  },
  {
    name: "sidebar_menu_profile_system",
    to: "/profile_system",
    icon: MagnifyingGlassIcon,
  },
  { name: "sidebar_sales_category", to: "/sales_category", icon: BiCategory },
  {
    name: "sidebar_menu_user",
    to: "/user",
    icon: BiUser,
  },
  {
    name: "Feedback",
    to: "/feedback",
    icon: MdFeedback,
  },
  {
    name: "mobile_users",
    to: "/mobile_user",
    icon: MdVerifiedUser,
  },
  {
    name: "sidebar_workshop_service",
    to: "/workshop-service",
    icon: AdjustmentsVerticalIcon,
  },
  {
    name: "sidebar_price_requests",
    to: "/price-requests",
    icon: MdOutlinePriceCheck,
  },
  {
    name: "sidebar_menu_integrations",
    icon: ArrowPathIcon,
    to: "/integrations",
    children: [
      { name: "sidebar_menu_integrations_akfa", to: "/integrations/akfa" },
      { name: "sidebar_menu_integrations_unit", to: "/integrations/unit" },
    ],
  },
  {
    name: "sidebar_app_versions",
    to: "/app-versions",
    icon: MdSystemUpdate,
  },
  {
    name: "sidebar_menu_localization",
    icon: MdTranslate,
    to: "/localization",
    children: [
      { name: "sidebar_menu_translations", to: "/translations" },
      { name: "sidebar_menu_languages", to: "/languages" },
    ],
  },
  {
    name: "sidebar_menu_tariff_feature",
    to: "/tariff-feature",
    icon: MdAttachMoney,
  },
];

const userNavigation = [
  { name: "Your Profile", href: "#" },
  { name: "Settings", href: "#" },
  {
    name: "Log out",
    href: "/",
  },
];

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

function Sidebar({ t }) {
  // const [open, setOpen] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [navigations, setNavigations] = useState(navigation);
  const [akfaPendingCount, setAkfaPendingCount] = useState(0);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { token } = useSelector((state) => state.user);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    const poll = async () => {
      const response = await getAkfaSummary(token);
      if (!cancelled && response.success) {
        setAkfaPendingCount(response.data?.pendingPriceCount || 0);
      }
    };
    poll();
    const interval = setInterval(poll, AKFA_BADGE_POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [token]);

  const goToAkfaBadge = (e) => {
    e.preventDefault();
    e.stopPropagation();
    navigate("/integrations/akfa");
  };

  const renderAkfaBadge = (item) =>
    item.to === "/price-requests" &&
    akfaPendingCount > 0 && (
      <span
        onClick={goToAkfaBadge}
        title={t("sidebar_akfa_badge_title")}
        className="ml-auto inline-flex items-center justify-center min-w-[1.25rem] px-1.5 py-0.5 text-xs font-bold text-white bg-red-600 rounded-full hover:bg-red-500"
      >
        {akfaPendingCount}
      </span>
    );

  return (
    <>
      <div>
        <Transition.Root show={sidebarOpen} as={Fragment}>
          <Dialog
            as="div"
            className="relative z-40 xl:hidden"
            onClose={setSidebarOpen}
          >
            <Transition.Child
              as={Fragment}
              enter="transition-opacity ease-linear duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="transition-opacity ease-linear duration-300"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-gray-600 bg-opacity-75" />
            </Transition.Child>

            <div className="fixed inset-0 z-40 flex">
              <Transition.Child
                as={Fragment}
                enter="transition ease-in-out duration-300 transform"
                enterFrom="-translate-x-full"
                enterTo="translate-x-0"
                leave="transition ease-in-out duration-300 transform"
                leaveFrom="translate-x-0"
                leaveTo="-translate-x-full"
              >
                <Dialog.Panel className="relative flex flex-col flex-1 w-full max-w-xs pt-5 pb-4 bg-gray-800">
                  <Transition.Child
                    as={Fragment}
                    enter="ease-in-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in-out duration-300"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                  >
                    <div className="absolute top-0 right-0 pt-2 -mr-12">
                      <button
                        type="button"
                        className="flex items-center justify-center w-10 h-10 ml-1 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
                        onClick={() => setSidebarOpen(false)}
                      >
                        <span className="sr-only">Close sidebar</span>
                        <XMarkIcon
                          className="w-6 h-6 text-white"
                          aria-hidden="true"
                        />
                      </button>
                    </div>
                  </Transition.Child>
                  <div className="flex items-center flex-shrink-0 px-4">
                    {/* <img
                      className="w-auto h-8"
                      src="https://tailwindui.com/img/logos/workflow-mark-indigo-600.svg"
                      alt="Construction"
                    /> */}
                    <h2 className="pl-2 text-lg font-extrabold text-white">
                      Construction Admin
                    </h2>
                  </div>
                  <div className="flex-1 h-0 mt-5 overflow-y-auto">
                    <nav className="px-2 space-y-1">
                      {navigations.map((item) =>
                        !item.children ? (
                          <Link
                            key={item.name}
                            to={item.to}
                            className={classNames(
                              item.to === location.pathname
                                ? "bg-gray-900 text-white"
                                : "text-gray-300 hover:bg-gray-700 hover:text-white",
                              "group flex items-center px-2 py-2 text-base font-medium rounded-md"
                            )}
                          >
                            <item.icon
                              className={classNames(
                                item.to === location.pathname
                                  ? "text-gray-300"
                                  : "text-gray-400 group-hover:text-gray-300",
                                "mr-4 flex-shrink-0 h-6 w-6"
                              )}
                              aria-hidden="true"
                            />
                            {t(`${item.name}`)}
                            {renderAkfaBadge(item)}
                          </Link>
                        ) : (
                          <Disclosure
                            as="div"
                            key={item.name}
                            className="space-y-1"
                          >
                            {({ open }) => (
                              <>
                                <Disclosure.Button className="flex items-center justify-between w-full px-2 py-2 text-sm font-medium text-gray-300 rounded-md hover:bg-gray-700 hover:text-white group">
                                  <div className="flex items-center">
                                    <item.icon
                                      className="flex-shrink-0 w-6 h-6 mr-3 text-gray-400 group-hover:text-gray-500"
                                      aria-hidden="true"
                                    />
                                    <span> {t(`${item.name}`)}</span>
                                  </div>
                                  <svg
                                    className={classNames(
                                      open
                                        ? "text-gray-400 rotate-90"
                                        : "text-gray-300",
                                      "ml-3 h-7 w-8 font-bold flex-shrink-0 transform transition-colors duration-150 ease-in-out group-hover:text-gray-400"
                                    )}
                                    viewBox="0 0 20 20"
                                  >
                                    <path
                                      d="M6 6L14 10L6 14V6Z"
                                      fill="currentColor"
                                    />
                                  </svg>
                                </Disclosure.Button>
                                <Disclosure.Panel className="space-y-1">
                                  {item.children.map((subItem) => (
                                    <Link
                                      key={subItem.name}
                                      to={subItem.to}
                                      className={classNames(
                                        subItem.to === location.pathname
                                          ? "bg-gray-900 text-white"
                                          : "text-gray-300 hover:bg-gray-700 hover:text-white",
                                        "group flex w-full items-center rounded-md py-2 pl-10 pr-2 text-sm font-medium text-white hover:bg-gray-50 hover:text-gray-900"
                                      )}
                                    >
                                      {t(`${subItem.name}`)}
                                    </Link>
                                  ))}
                                </Disclosure.Panel>
                              </>
                            )}
                          </Disclosure>
                        )
                      )}
                    </nav>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
              <div className="flex-shrink-0 w-14" aria-hidden="true">
                {/* Dummy element to force sidebar to shrink to fit close icon */}
              </div>
            </div>
          </Dialog>
        </Transition.Root>

        {/* Static sidebar for desktop */}
        <div className="hidden xl:flex md:w-64 md:flex-col md:fixed md:inset-y-0">
          {/* Sidebar component, swap this element with another sidebar if you like */}
          <div className="flex flex-col flex-1 min-h-0 bg-gray-800">
            <div className="flex items-center flex-shrink-0 h-16 px-4 bg-gray-900">
              {/* <img
                className="w-auto h-8"
                src="https://tailwindui.com/img/logos/workflow-mark-indigo-600.svg"
                alt="Construction"
              /> */}
              <h2 className="pl-2 text-lg font-extrabold text-white">
                Construction Admin
              </h2>
            </div>
            <div className="flex flex-col flex-1 overflow-y-auto">
              <nav className="flex-1 px-2 py-4 space-y-1">
                {navigations.map((item) =>
                  !item.children ? (
                    <Link
                      key={item.name}
                      to={item.to}
                      className={classNames(
                        item.to === location.pathname
                          ? "bg-gray-900 text-white"
                          : "text-gray-300 hover:bg-gray-700 hover:text-white",
                        "group flex items-center px-2 py-2 text-sm font-medium rounded-md"
                      )}
                    >
                      <item.icon
                        className={classNames(
                          item.to === location.pathname
                            ? "text-gray-300"
                            : "text-gray-400 group-hover:text-gray-300",
                          "mr-3 flex-shrink-0 h-6 w-6"
                        )}
                        aria-hidden="true"
                      />
                      {t(`${item.name}`)}
                      {renderAkfaBadge(item)}
                    </Link>
                  ) : (
                    <Disclosure as="div" key={item.to} className="space-y-1">
                      {({ open }) => (
                        <>
                          <>{open}</>
                          <Disclosure.Button className="flex items-center justify-between w-full px-2 py-2 text-sm font-medium text-gray-300 rounded-md hover:bg-gray-700 hover:text-white group">
                            <div className="flex items-center">
                              <item.icon
                                className="flex-shrink-0 w-6 h-6 mr-3 text-gray-400 group-hover:text-gray-500"
                                aria-hidden="true"
                              />
                              <span> {t(`${item.name}`)}</span>
                            </div>
                            <svg
                              className={classNames(
                                open
                                  ? "text-gray-400 rotate-90"
                                  : "text-gray-300",
                                "ml-3 h-7 w-7 font-bold flex-shrink-0 transform transition-colors duration-150 ease-in-out group-hover:text-gray-400"
                              )}
                              viewBox="0 0 20 20"
                            >
                              <path
                                d="M6 6L14 10L6 14V6Z"
                                fill="currentColor"
                              />
                            </svg>
                          </Disclosure.Button>
                          <Disclosure.Panel className="space-y-1">
                            {item.children.map((subItem) => (
                              <Link
                                key={subItem.name}
                                to={subItem.to}
                                className={classNames(
                                  subItem.to === location.pathname
                                    ? "bg-gray-900 text-white"
                                    : "text-gray-300 hover:bg-gray-700 hover:text-white",
                                  "group flex w-full items-center rounded-md py-2 pl-10 pr-2 text-sm font-medium text-white hover:bg-gray-50 hover:text-gray-900"
                                )}
                              >
                                {t(`${subItem.name}`)}
                              </Link>
                            ))}
                          </Disclosure.Panel>
                        </>
                      )}
                    </Disclosure>
                  )
                )}
              </nav>
            </div>
          </div>
        </div>
        <div className="flex flex-col xl:pl-64">
          <div className="sticky top-0 z-10 flex flex-shrink-0 h-16 bg-white shadow">
            <button
              type="button"
              className="px-4 text-gray-500 border-r border-gray-200 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500 xl:hidden"
              onClick={() => setSidebarOpen(true)}
            >
              <span className="sr-only">Open sidebar</span>
              <Bars3BottomLeftIcon className="w-6 h-6" aria-hidden="true" />
            </button>
            <div className="flex justify-between flex-1 px-4">
              <div className="flex flex-1">
                <form className="flex w-full md:ml-0" action="#" method="GET">
                  <label htmlFor="search-field" className="sr-only">
                    Search
                  </label>
                  <div className="relative w-full text-gray-400 focus-within:text-gray-600">
                    <div className="absolute inset-y-0 left-0 flex items-center pointer-events-none">
                      <MagnifyingGlassIcon
                        className="w-5 h-5"
                        aria-hidden="true"
                      />
                    </div>
                    <input
                      id="search-field"
                      className="block w-full h-full py-2 pl-8 pr-3 text-gray-900 placeholder-gray-500 border-transparent focus:outline-none focus:placeholder-gray-400 focus:ring-0 focus:border-transparent sm:text-sm"
                      placeholder="Search"
                      type="search"
                      name="search"
                    />
                  </div>
                </form>
              </div>
              <div className="flex items-center ml-4 md:ml-6">
                <DropdownLanguage />
                {/* Profile dropdown */}
                <Menu as="div" className="relative ml-3">
                  <div>
                    <Menu.Button className="flex items-center max-w-xs text-sm bg-white rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
                      <span className="sr-only">Open user menu</span>
                      <img
                        className="w-8 h-8 rounded-full"
                        src="https://cdn-icons-png.flaticon.com/512/149/149071.png"
                        alt=""
                      />
                    </Menu.Button>
                  </div>
                  <Transition
                    as={Fragment}
                    enter="transition ease-out duration-100"
                    enterFrom="transform opacity-0 scale-95"
                    enterTo="transform opacity-100 scale-100"
                    leave="transition ease-in duration-75"
                    leaveFrom="transform opacity-100 scale-100"
                    leaveTo="transform opacity-0 scale-95"
                  >
                    <Menu.Items className="absolute right-0 w-48 py-1 mt-2 origin-top-right bg-white rounded-md shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                      {userNavigation.map((item) => (
                        <Menu.Item
                          key={item.name}
                          onClick={() => {
                            if (item.name === "Log out") {
                              dispatch(logOut());
                            }
                          }}
                        >
                          {({ active }) => (
                            <Link
                              to={item.href}
                              className={classNames(
                                active ? "bg-gray-100" : "",
                                "block px-4 py-2 text-sm text-gray-700"
                              )}
                            >
                              {item.name}
                            </Link>
                          )}
                        </Menu.Item>
                      ))}
                    </Menu.Items>
                  </Transition>
                </Menu>
              </div>
            </div>
          </div>
          {/* main page */}
          <MainRoutes />
          {/* main page */}
        </div>
      </div>
    </>
  );
}

export default withTranslation()(Sidebar);
